import React, { useRef, useCallback } from 'react';
import noop from 'lodash/noop';

import FinalForm, { Field, type TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm';
import FormEmailInput from '@/creditflowresearch/ui/common/components/EmailInput';
import Textarea from '@creditflowresearch/uilib/Textarea';
import UploadFile from '@/creditflowresearch/ui/common/components/UploadFile';
import FinsightModal from '@/creditflowresearch/ui/common/components/FinsightModal';
import ReCaptcha from '@framework/ui/Molecules/Form/ReCaptcha';
import Button from '@creditflowresearch/uilib/Button';
import Flex from '@creditflowresearch/uilib/Flex';
import { useMissingSomethingModalContext } from '@/creditflowresearch/application/MissingSomethingModalContext';

import validate from '@/creditflowresearch/ui/common/components/FinsightModal/modalValidator';
import { MISSING_SOMETHING_MODAL_FORM, SUBMIT_MISSING_SOMETHING_MODAL_FORM } from './constants';

import styles from './missingSomethingModalForm.module.scss';
import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';

const MissingSomethingModalForm = () => {
  const {
    submitForm,
    resetModal,
    hideModal,
    isVisible,
    isFetching,
    isSubmitSucceed,
  } = useMissingSomethingModalContext();
  let resetFinalFormState = noop;
  const inputUploadFile = useRef(null);

  const onCloseClicked = useCallback(
    () => {
      inputUploadFile.current.deleteFile();
      resetFinalFormState();
      hideModal();
      resetModal();
    },
    [resetFinalFormState, resetModal, hideModal],
  );

  const renderFields = useCallback<TFinalFormRenderProp>(
    ({
      valid,
      form,
    }) => {
      resetFinalFormState = form.restart;
      return (
        <>
          <div className={ styles.headerText }>
            <h1>Missing something? Tell us</h1>
            <p className={ styles.modalDescription }>
              Report a missing deal or ask us a question! All feedback, ideas, and suggestions are welcome.
            </p>
          </div>
          <Field
            name="email"
            type="email"
            className={ styles.inputContainer }
            component={ FormEmailInput }
            dataTest="missingSomethingModalFormEmailInput"
          />
          <Field
            textareaClassName={ styles.textarea }
            placeholder="Please provide comments here"
            name="comments"
            component={ Textarea }
            dataTest="missingSomethingModalFormTextArea"
          />
          <UploadFile
            formName={ MISSING_SOMETHING_MODAL_FORM }
            ref={ inputUploadFile }
          />
          { /* We need the check below because ReCaptcha is suspected in flooding sentry with errors, see FIN-10487 */ }
          { isVisible && (
            <div className={ spacesStyles.mbxl }>
              <Field
                name="recaptcha"
                component={ ReCaptcha }
              />
            </div>
          ) }
          <Flex align="center" gap={ 10 }>
            <Button
              dataTest={ SUBMIT_MISSING_SOMETHING_MODAL_FORM }
              type="submit"
              disabled={ !valid }
              variant="solid"
            >
              Submit
            </Button>
            <Button
              dataTest="cancelButton"
              className={ styles.cancelButton }
              variant="text"
              onClick={ onCloseClicked }
            >
              Cancel
            </Button>
          </Flex>
        </>
      );
    },
    [isVisible, onCloseClicked, resetFinalFormState],
  );

  const onSumbit = useCallback(
    (payload) => submitForm(inputUploadFile.current.getFile(), payload),
    [submitForm],
  );

  return (
    <FinsightModal
      hideOnOverlayClicked
      successText="Your feedback has been submitted."
      // @ts-ignore
      modalDescription="Report a missing deal or ask us a question! All feedback, ideas, and suggestions are welcome."
      modalTitle={ <>Instant Market<br />Color</> }
      onCloseClicked={ onCloseClicked }
      isVisible={ isVisible }
      isFetching={ isFetching }
      isSubmitSucceed={ isSubmitSucceed }
      dataTest="missingSomethingModal"
    >
      <FinalForm
        dataTest={ MISSING_SOMETHING_MODAL_FORM }
        onSubmit={ onSumbit }
        render={ renderFields }
        validate={ validate }
      />
    </FinsightModal>
  );
};

export default MissingSomethingModalForm;
